import type { Event, Exception, StackFrame } from '@sentry/core';

const TAG_MANAGER_URL_REGEX = /googletagmanager\.com\//;

// Globals that GTM tags and scraper-injected scripts call when their vendor script never loaded.
// googletag is already in ignoreErrors. See SSR-1396.
const INJECTED_GLOBALS = [
  'gtag',
  'dfp_cattype',
  'pbjs',
  'ym',
  String.raw`yaCounter\d+`,
  'olytics',
  'MsCrmMkt',
  'CAFE24API',
  'wwTrack',
  'jrConfigGlobal',
  '_LT',
  'jtag',
  'FS',
  'gsc',
  'ACC',
  'getScript',
  'extDomain',
  'xbrowser',
  'swbrowser',
].join('|');

const INJECTED_GLOBAL_ERROR_REGEX = new RegExp(
  `^(?:(?:${INJECTED_GLOBALS}) is not defined|Can't find variable: (?:${INJECTED_GLOBALS}))$`
);

const isAnonymous = ({ filename }: StackFrame): boolean => filename === '<anonymous>';

type SerializedRejection = { code?: unknown; message?: unknown; sentMessage?: { type?: unknown } };

const isJsonRpcErrorCode = (code: unknown): boolean =>
  Number.isInteger(code) && (code as number) >= -32768 && (code as number) <= -32000;

// Plain-object rejections with no stack: a consent (GPP) stub that scrapers inject (SSR-14WB), a
// Safari Web Extension's message channel (SSR-7R, the extension behind SSR-22), and a wallet
// extension's JSON-RPC error, whose codes fall in the range JSON-RPC reserves (SSR-5E).
const isInjectedRejection = ({ extra }: Event): boolean => {
  // eslint-disable-next-line no-underscore-dangle -- the Sentry SDK's key for a non-Error value
  const value = extra?.__serialized__ as SerializedRejection | undefined;
  return (
    value?.message === 'GPP CMP not found' ||
    value?.sentMessage?.type === 'user:action' ||
    isJsonRpcErrorCode(value?.code)
  );
};

const IOS_CHROMIUM_USER_AGENT_REGEX = /\b(?:CriOS|GSA)\//;

const withoutQueryOrHash = (url: string): string => url.split(/[?#]/)[0];

// Chrome and the Google app on iOS inject a script whose frames carry the page URL. It recurses
// until the stack overflows, on every page. See SSR-12TF.
const isIosChromiumStackOverflow = (
  { request }: Event,
  { type, value, stacktrace }: Exception
): boolean => {
  if (type !== 'RangeError' || !value?.startsWith('Maximum call stack size exceeded')) {
    return false;
  }
  const pageUrl = request?.url;
  const userAgent = request?.headers?.['User-Agent'];
  if (!pageUrl || !userAgent || !IOS_CHROMIUM_USER_AGENT_REGEX.test(userAgent)) {
    return false;
  }
  const frames = stacktrace?.frames ?? [];
  return (
    frames.length > 0 &&
    frames.every(
      ({ filename }) =>
        filename !== undefined && withoutQueryOrHash(filename) === withoutQueryOrHash(pageUrl)
    )
  );
};

const isInjectedGlobalError = ({ mechanism, value }: Exception): boolean =>
  mechanism?.handled === false && INJECTED_GLOBAL_ERROR_REGEX.test(value ?? '');

/**
 * Matches stacks whose innermost frame is eval'd code: `<anonymous>` with a line number, which
 * built-ins such as `JSON.parse` lack. The last named frame must be GTM, the outermost frame
 * (often the SDK's timer wrapper in our bundle), or absent. Sentry orders frames outermost first.
 * See SSR-14VC (GTM) and SSR-1TG (timers).
 */
const isInjectedStack = (frames: StackFrame[]): boolean => {
  const innermost = frames[frames.length - 1];
  if (innermost === undefined || !isAnonymous(innermost) || innermost.lineno === undefined) {
    return false;
  }
  const lastNamed = frames.map(isAnonymous).lastIndexOf(false);
  return lastNamed <= 0 || TAG_MANAGER_URL_REGEX.test(frames[lastNamed].filename ?? '');
};

// A scraper runs our bundle in a document with no <body>, which a parsed page always has. Next.js
// runs async chunks while <head> is still parsing, so a document that is still loading doesn't
// count. See SSR-143B, SSR-14F2, SSR-14ZS and SSR-ROVER-10SE.
const isBodylessDocument = (): boolean =>
  // eslint-disable-next-line rover/no-platform-specific-globals-or-imports -- browser Sentry only, and Turbopack builds don't define JS_ENV_CLIENT
  typeof document !== 'undefined' && document.body === null && document.readyState !== 'loading';

export default function isInjectedScriptError(event: Event): boolean {
  if (isBodylessDocument() || isInjectedRejection(event)) {
    return true;
  }
  return (event.exception?.values ?? []).some(
    (exception) =>
      isInjectedGlobalError(exception) ||
      isInjectedStack(exception.stacktrace?.frames ?? []) ||
      isIosChromiumStackOverflow(event, exception)
  );
}
